import { sql } from '@w/db'
import type { WorkspaceDb } from './workspace-tables'


export function cardReadColumns(table: WorkspaceDb['flashcards']) {
  return table.all
}

/** Pending peer deletions stay live; accepted and direct deletions remain hidden. */
export function cardVisibleTo(table: WorkspaceDb['flashcards'], userId: string) {
  return sql`${table.userId.eq(userId)} AND ${table.deletedAt.isNull()}`
}

/**
 * Pages another user can read, from what this replica holds: that user's grants (a teacher's replica receives them on
 * pages it has Full access to, workspace-replication.ts canRead) and their inheriting child pages, as the server's
 * effectivePageLevelsQuery walks them. A workspace admin's implicit access is not in the replica.
 */
export function readablePagesOf(userId: string) {
  return sql`(WITH RECURSIVE readable(pageId,depth) AS (
      SELECT g.pageId,0 FROM pagePermissions g WHERE g.principalKind='user' AND g.principalId=${userId} AND g.level>=1
      UNION SELECT r.toPageId,d.depth+1 FROM readable d JOIN pageRelations r ON r.fromPageId=d.pageId AND r.type='child'
        JOIN pages c ON c.id=r.toPageId AND c.inheritance='inherit' WHERE d.depth<256)
    SELECT pageId FROM readable)`
}

/** Cards are authored rows. Reading never derives card state from question blocks. */
export function learnerCards(userId: string, pages?: ReturnType<typeof sql>) {
  const removal = sql`COALESCE((SELECT 'page@'||p.deletedAt FROM pages p WHERE p.id=g.pageId AND p.deletedAt IS NOT NULL),g.node||'@'||g.deletedAt)`
  const pending = sql`p.acceptedRemoval IS NOT NULL AND p.acceptedRemoval IS NOT (${removal})`
  return sql`(SELECT ${userId} AS userId,g.qid,
    COALESCE(p.pageId,g.pageId) AS pageId,
    CASE WHEN p.pageId IS NOT NULL AND p.pageId IS NOT g.pageId THEN p.node ELSE g.node END AS node,
    CASE WHEN p.pageId IS NOT NULL AND p.pageId IS NOT g.pageId THEN p.fractionalIndex ELSE g.fractionalIndex END AS fractionalIndex,
    CASE WHEN p.qid IS NULL THEN g.importance ELSE p.importance END AS importance,
    CASE WHEN p.qid IS NULL THEN g.difficulty ELSE p.difficulty END AS difficulty,
    CASE WHEN p.qid IS NULL THEN g.promptType ELSE p.promptType END AS promptType,
    CASE WHEN p.qid IS NULL THEN g.ttaSeconds ELSE p.ttaSeconds END AS ttaSeconds,
    CASE WHEN p.qid IS NULL THEN g.cardKind ELSE p.cardKind END AS cardKind,
    COALESCE(p.stage,0) AS stage,COALESCE(p.repeatAt,'now') AS repeatAt,
    COALESCE(p.graduated,0) AS graduated,p.frozenAt,p.touchedAt,
    CASE WHEN p.qid IS NULL THEN g.deletedAt WHEN p.pageId IS NOT g.pageId THEN p.deletedAt WHEN ${pending} THEN NULL ELSE p.deletedAt END AS deletedAt,
    CASE WHEN p.qid IS NULL THEN g.deletedBy WHEN p.pageId IS NOT g.pageId THEN p.deletedBy WHEN ${pending} THEN g.deletedBy ELSE COALESCE(p.deletedBy,CASE WHEN p.touchedAt IS NOT NULL THEN g.deletedBy END) END AS deletedBy,
    COALESCE(p.updatedAt,g.updatedAt) AS updatedAt,p.acceptedRemoval,COALESCE(p.reviewCount,0) AS reviewCount
    FROM genericCards g LEFT JOIN flashcards p ON p.qid=g.qid AND p.userId=${userId}
    WHERE (g.deletedAt IS NULL OR p.qid IS NOT NULL) ${pages ? sql`AND COALESCE(p.pageId,g.pageId) IN ${pages}` : sql``}
    UNION ALL SELECT p.userId,p.qid,p.pageId,p.node,p.fractionalIndex,p.importance,p.difficulty,p.promptType,p.ttaSeconds,p.cardKind,p.stage,p.repeatAt,p.graduated,p.frozenAt,p.touchedAt,p.deletedAt,p.deletedBy,p.updatedAt,p.acceptedRemoval,p.reviewCount FROM flashcards p WHERE p.userId=${userId}
      AND NOT EXISTS(SELECT 1 FROM genericCards g WHERE g.qid=p.qid)
      ${pages ? sql`AND p.pageId IN ${pages}` : sql``}) AS flashcards`
}
